/**
 * Connected-Data Acquisition Action Definitions
 *
 * Phase 5 of `gibbs-bmaps-capability-coverage`. Binds 5 unreachable
 * UserActions in `proto/src/cmds/ConnectedDataCmds.js`, plus collapses
 * the 4-way `Export*ToFormat` family into one `export-to-format`
 * ActionDef with a `target` parameter.
 *
 * **Note on `Export*ToFormat` consolidation:**
 * The four BMaps UserActions `ExportToFormat`, `ExportQueryToFormat`,
 * `ExportCompoundsToFormat`, `ExportSelectionToFormat` differ only in
 * the source of the export payload (the whole workspace, a search
 * query result, the loaded compounds, or the current selection). Rather
 * than four distinct ActionDefs that differ in `userAction:` field,
 * this one `export-to-format` ActionDef has a `target: enum` parameter
 * that the dispatcher uses to pick the right UserAction at runtime. The
 * `userAction:` field is `ExportCompoundsToFormat` (the most common
 * case), and the dispatcher's `extractParams` step adjusts the dispatch
 * target based on the `target` parameter value.
 *
 * The audit harness counts `ExportCompoundsToFormat` as bound. The
 * other three (`ExportToFormat`, `ExportQueryToFormat`,
 * `ExportSelectionToFormat`) remain in `followups.md` as "reached
 * transitively via export-to-format with target=...".
 *
 * @module gibbs/registry/actions/connected-data-acquisition
 */

import { param } from '../schema';

// ─── Map / connection fetching ─────────────────────────────────────────────

export const fetchMaps = {
    id: 'fetch-maps',
    name: 'Fetch Maps from All Connections',
    category: 'External Data Retrieval',
    description: 'Fetch the catalog of available protein maps from every active data connection. Use when the user wants to see "what proteins are available?" before loading one. Returns an aggregated list across all connections.',
    inputs: [],
    outputs: [
        param('mapList', 'object', 'Aggregated list of available maps across all data connections'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FetchMaps',
    serverCommand: null,
    jobType: null,
};

export const fetchConnectionMaps = {
    id: 'fetch-connection-maps',
    name: 'Fetch Maps from One Connection',
    category: 'External Data Retrieval',
    description: 'Fetch the available protein maps from a specific data connection. Useful when the user knows which provider they want to query.',
    inputs: [
        param('dataConnection', 'object', 'The DataConnection to query', true),
    ],
    outputs: [
        param('mapList', 'object', 'List of maps from this connection'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FetchConnectionMaps',
    serverCommand: null,
    jobType: null,
};

// ─── Molecule loading ──────────────────────────────────────────────────────

export const loadMoleculeFromConnection = {
    id: 'load-molecule-from-connection',
    name: 'Load Molecule from Connection',
    category: 'External Data Retrieval',
    description: 'Load a molecule from a connected data source (e.g., an external compound library). Distinct from `load-compound`, which takes locally-supplied compound data. Use when the user names a compound by an external identifier the connection knows how to resolve.',
    inputs: [],
    outputs: [
        param('compound', 'Compound', 'The loaded compound'),
    ],
    parameters: [
        param('molId', 'string', 'Compound identifier (name or external ID)', true),
        param('molFormat', 'string', 'Source format of the compound data', false, { options: ['smiles', 'sdf', 'mol', 'pdb', 'inchi'] }),
        param('molData', 'string', 'Inline compound data (if not fetching by ID)', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'LoadMolecule',
    serverCommand: null,
    jobType: null,
};

// ─── Structure-of-interest target list ─────────────────────────────────────

export const addStructureOfInterest = {
    id: 'add-structure-of-interest',
    name: 'Add Structure of Interest',
    category: 'Target Setup',
    description: 'Add a connected-data protein structure to the project target/off-target list without necessarily loading it into the scene. When possible, BMaps records the alignment needed for later fragment-data queries.',
    inputs: [
        param('mapCase', 'object', 'MapCase for the structure selected from connected data', true),
    ],
    outputs: [],
    parameters: [
        param('isTarget', 'boolean', 'Add as a primary target rather than an off-target/reference structure', false, { defaultValue: true }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'AddStructureOfInterest',
    serverCommand: null,
    jobType: null,
};

// ─── Export (consolidates Export*ToFormat × 4) ─────────────────────────────

export const exportToFormat = {
    id: 'export-to-format',
    name: 'Export to File Format',
    category: 'Export & Reporting',
    description: 'Export workspace content to a file in one of several formats. The `target` parameter picks what to export — all loaded compounds, the result of a search query, a specific compound list, or the current selection. The `format` parameter picks the file format.',
    inputs: [],
    outputs: [],
    parameters: [
        param('target', 'string', 'Which content to export', true, {
            defaultValue: 'compounds',
            options: ['compounds', 'query', 'selection', 'all'],
        }),
        param('format', 'string', 'Output file format', true, {
            options: ['sdf', 'mol', 'pdb', 'smiles', 'csv'],
        }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    // The audit binds the most common variant. The dispatcher routes
    // to ExportToFormat / ExportQueryToFormat / ExportSelectionToFormat
    // when target !== 'compounds'. See module header comment.
    userAction: 'ExportCompoundsToFormat',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ────────────────────────────────────────────────────────

export default [
    fetchMaps,
    fetchConnectionMaps,
    loadMoleculeFromConnection,
    addStructureOfInterest,
    exportToFormat,
];
